<template>
  <div class="mt-2 mb-2">
    <el-upload
      ref="inputUpload"
      list-type="picture-card"
      class="hide-upload-btn"
      :action="actionUrl"
      :data="{ barcodeId }"
      :with-credentials="true"
      :multiple="true"
      :file-list="fileList"
      :before-remove="beforeRemove"
      :on-change="onChanged"
      :before-upload="preupload"
      :on-remove="onRemove"
      :on-preview="onPreview"
      :http-request="httpRequestController"
      :on-error="onError"
      :accept="isImageFileKind ? imageOption : '*'"
      :name="'file'"
      :auto-upload="!isBarcodeScanFrame"
    >
      <template v-if="!isBarcodeScanFrame" #default>
        <el-button v-show="false" ref="triggerFileDialogBtn"></el-button>
        <div class="action-button" v-show="false">
          <component :is="component" color="white" />
          {{ getImageLabel }}
        </div>
      </template>

      <template v-if="!isImageFileKind || isScannedBCAvail" #file="{ file }">
        <!-- thumbnail -->
        <template v-if="file?.status === 'success'">
          <img v-if="fileIsImage(file)" class="el-upload-list__item-thumbnail" :src="file.url" alt="" />
          <div v-else class="flex flex-col items-center margin-30 h-1/2">
            <component :is="component" color="white" />
            <span class="text-black truncate w-1/2">{{ file.name }}</span>
          </div>

          <a class="el-upload-list__item-name"><i class="el-icon-document"></i></a>

          <!-- success checked -->
          <label v-if="file?.response" class="el-upload-list__item-status-label">
            <i class="el-icon-upload-success el-icon-check"></i>
          </label>
        </template>

        <!-- progress-bar -->
        <template v-else-if="file?.status === 'uploading'">
          <el-progress type="circle" :stroke-width="6" :percentage="file?.percentage" />
        </template>

        <!-- actions -->
        <span class="el-upload-list__item-actions">
          <span class="el-upload-list__item-preview" @click="onPreview(file)"><i class="el-icon-zoom-in"></i></span>
          <span class="el-upload-list__item-delete" @click="$refs.inputUpload.handleRemove(file)">
            <i class="el-icon-delete"></i>
          </span>
        </span>
      </template>
    </el-upload>

    <div v-if="!isBarcodeScanFrame">
      <el-button @click="onUploadButtonClicked" class="uploader-btn">
        <component :is="component" color="white" />
        <div class="py-1">{{ getImageLabel }}</div>
      </el-button>
    </div>

    <el-button v-if="isBarcodeScanFrame && !isScannedBCAvail" class="uploader-btn" @click="onAddScannedBCImg">
      <IconScannedBarcode />
      <div class="py-1">{{ $t('barcode_scanned_image') }}</div>
    </el-button>
    <div class="barcode-number-block" v-if="!isBarcodeScanFrame">
      <span>
        {{ $t('Registered_Image_Amount') }}
      </span>
      <span class="label-box"> {{ fileCount }} </span>
    </div>
  </div>

  <CustomTakePhotoCamera
    v-model:isShowActionDialog="isTakePhotoDialog"
    :overlayImage="overlayImage"
    @update:captureImage="onCaptureImageUpdated($event)"
  />
  <el-dialog
    width="90%"
    :lock-scroll="true"
    top="0"
    :model-value="showDialogAlert"
    :close-on-click-modal="true"
    :show-close="false"
    @closed="onDialogClose()"
  >
    <div class="dialogMessage">
      {{ $t('no_scanned_barcode_warning') }}
    </div>
    <template #footer>
      <div class="dialog-footer display-in-center">
        <el-button
          type="primary"
          class="full-w-button el-button--primary scanned-barcode-warning-dialog-ok"
          @click="onDialogClose"
        >
          {{ $t('OK') }}
          <KeyenceHTHardKeyButton
            v-if="isKeyenceEnterpriseBrowser"
            :hardkey="KeyenceHardKey.ENTER"
            :function="onDialogClose"
            :visible="showDialogAlert"
            :newLayer="true"
          />
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script lang="ts">
import CustomTakePhotoCamera from '@/components/dialog/CustomTakePhotoCamera.vue'
import KeyenceHTHardKeyButton from '@/components/keyenceHT/hardkeys/KeyenceHTHardKeyButton.vue'
import IconFile from '@/components/svg/IconFile.vue'
import IconImage from '@/components/svg/IconImage.vue'
import IconScannedBarcode from '@/components/svg/IconScannedBarcode.vue'
import IconTakePhoto from '@/components/svg/IconTakePhoto.vue'
import { UPDATE_SCANNED_RESULT } from '@/store/actions'
import { baseUrl, customRequest, removeImage } from '@/utils/api'
import { IMAGE_EXTENSION, MAX_IMAGE_SIZE } from '@/utils/constants'
import { getMobileOS } from '@/utils/deviceHelper'
import { EFileKind } from '@/utils/enums'
import errorHandler from '@/utils/errorHandler'
import { KeyenceHardKey } from '@/utils/keyenceHT/keyenceHardKeyEventHandler'
import { isKeyenceEnterpriseBrowser } from '@/utils/keyenceHT/keyenceModules'
import { getFilePathFromURL, isFirefoxOrOperaAgent, makeid, resizeImage } from '@/utils/helpers'
import mitt from '@/utils/mitt'
import { isEmpty } from '@/utils/typeChecker'
import { IBarcodeSavedImageStatus, TError } from '@/utils/types/commonTypes'
import { IFile } from '@/utils/types/fieldData'
import { AxiosRequestConfig } from 'axios'
import {
  ElFile,
  ElUploadProgressEvent,
  ElUploadRequestOptions,
  UploadFile,
} from 'element-plus/lib/el-upload/src/upload.type'
import { Options, Vue } from 'vue-class-component'
import { Prop, Ref, Watch } from 'vue-property-decorator'

@Options({
  inheritAttrs: false,
  components: {
    IconImage,
    IconFile,
    IconTakePhoto,
    IconScannedBarcode,
    CustomTakePhotoCamera,
    KeyenceHTHardKeyButton,
  },
  emits: ['uploaded'],
  name: 'InputImageUpload',
})
export default class InputImageUpload extends Vue {
  KeyenceHardKey = KeyenceHardKey

  get isKeyenceEnterpriseBrowser() {
    return isKeyenceEnterpriseBrowser()
  }

  @Ref() readonly inputUpload!: unknown
  @Ref() readonly triggerFileDialogBtn!: unknown
  @Prop({ type: String }) readonly fieldName!: string
  @Prop({ type: String }) readonly label!: string
  @Prop({ type: String }) readonly fileKind!: string
  @Prop({ type: String }) readonly overlayImage?: string
  @Prop({ type: Boolean }) readonly isShowCounter!: boolean
  @Prop({ type: [String, Number, Array, Object, Date] }) readonly modelValue?: unknown

  imageUrls = new Set()
  listScannedBCFileFromStore: File[] = []
  listResponsedImage = [] as string[]
  totalImagesNeedProcess = 0

  fileList = [] as UploadFile[]
  showDialogAlert = false
  isTakePhotoDialog = false
  isBarcodeScanFrame = false

  private removeEmptyStrings() {
    this.imageUrls.forEach((url) => {
      if (url === '' || url == null) {
        this.imageUrls.delete(url)
      }
    })
    this.listResponsedImage = this.listResponsedImage.filter((url) => url !== '' && url != null)
    this.fileList = this.fileList.filter((file) => {
      return file.url !== '' && file.url != null
    })
  }

  get isSupportNativeImageOptions() {
    return getMobileOS() === 'iOS' || isFirefoxOrOperaAgent()
  }

  get imageOption() {
    return this.isSupportNativeImageOptions ? 'image/*' : 'image/*;capture=camera'
  }

  get getImageLabel() {
    return this.$t(this.isImageFileKind ? 'Pictures' : 'file_urls')
  }

  onCaptureImageUpdated(blob: Blob) {
    const uploadFile = new File([blob], `${makeid(16)}.png`, { type: 'image/png' })
    this.$refs.inputUpload.handleStart(uploadFile)
    this.$refs.inputUpload.submit()
  }

  onUploadButtonClicked() {
    if (this.isPhotoOnlyKind) {
      this.isTakePhotoDialog = true
      return
    }

    this.$refs.triggerFileDialogBtn.$el.click()
  }

  get component() {
    return this.isImageFileKind ? IconImage : IconFile
  }

  get isImageFileKind() {
    return [EFileKind.IMAGE, EFileKind.BARCODE_SCAN_FRAME, EFileKind.PHOTO_ONLY].find((key) => this.fileKind === key)
  }

  get isScannedBarcodeFrame() {
    return this.fileKind === EFileKind.BARCODE_SCAN_FRAME
  }

  get isScannedBCAvail() {
    return !isEmpty(this.fileList)
  }

  get fileCount() {
    return Array.from(this.imageUrls).length || 0
  }

  get barcodeId() {
    return this.$store.state.barcode.barcode?.id
  }

  get actionUrl() {
    return `${baseUrl}/barcode/file`
  }

  get scannedBarcodeBlobsFromLocalStorage() {
    if (this.$store.getters.scannedBarcodeBlob && this.$store.getters.scannedBarcodeBlob.length > 0) {
      return this.$store.getters.scannedBarcodeBlob
    }
    const scannedBarcodeBlobsLocal = localStorage.getItem(this.keyBarcodeScanChanged)
    let scannedBarcodeBlobsLocalParsed = null
    if (scannedBarcodeBlobsLocal) {
      try {
        const parsedData = JSON.parse(scannedBarcodeBlobsLocal)
        if (Array.isArray(parsedData) && parsedData.length > 0 && typeof parsedData[0] === 'string') {
          scannedBarcodeBlobsLocalParsed = parsedData.map((base64: string) => this.base64ToBlob(base64))
        } else {
          scannedBarcodeBlobsLocalParsed = parsedData
        }
      } catch (error) {
        console.error('Error parsing localStorage data:', error)
        scannedBarcodeBlobsLocalParsed = null
      }
    }
    this.$store.dispatch(UPDATE_SCANNED_RESULT, scannedBarcodeBlobsLocalParsed)
    return scannedBarcodeBlobsLocalParsed
  }

  get scannedBCFile() {
    const scannedFiles: File[] = []
    const scannedBarcodeBlobs = this.scannedBarcodeBlobsFromLocalStorage
    if (!scannedBarcodeBlobs || scannedBarcodeBlobs.length === 0) {
      return null
    }
    for (let i = 0; i < scannedBarcodeBlobs.length; i++) {
      const scannedBarcodeBlob = scannedBarcodeBlobs[i]
      const newFile = new File([scannedBarcodeBlob], `${this.barcodeId}_${i}.png`, { type: 'image/png' })
      scannedFiles.push(newFile)
    }

    if (this.listScannedBCFileFromStore.length === 0) {
      this.listScannedBCFileFromStore = [...scannedFiles]
    }
    return scannedFiles
  }

  get scannedBarcodeStatus(): Record<string, IBarcodeSavedImageStatus> {
    return this.$store.state.barcode.scannedBarcodeStatus || {}
  }

  @Watch('$store.state.barcode.scannedBarcodeStatus')
  uploadBCImage() {
    if (!this.scannedBarcodeStatus?.[this.fieldName]) return

    if (this.scannedBarcodeStatus?.[this.fieldName]?.isReady && this.isScannedBCAvail && !this.fileList[0].response) {
      if (this.listScannedBCFileFromStore) {
        for (let i = 0; i < this.listScannedBCFileFromStore?.length; i++) {
          const file = this.listScannedBCFileFromStore[i]
          this.$refs.inputUpload.handleStart(file)
        }
      }
      this.$refs.inputUpload.submit()
    } else {
      this.$emit('uploaded', [{ url: 'none' }])
    }
  }

  onDialogClose() {
    this.showDialogAlert = false
  }

  // Helper method to convert Blobs to base64 and save to localStorage
  async saveBlobsToLocalStorage(blobs: Blob[]) {
    if (!blobs || blobs.length === 0) return

    try {
      const base64Array = await Promise.all(blobs.map((blob) => this.blobToBase64(blob)))
      localStorage.setItem(this.keyBarcodeScanChanged, JSON.stringify(base64Array))
    } catch (error) {
      console.error('Error saving blobs to localStorage:', error)
    }
  }

  // Helper method to convert Blob to base64 string
  blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        const result = reader.result as string
        resolve(result)
      }
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  }

  // Helper method to convert base64 back to Blob
  base64ToBlob(base64: string): Blob {
    const arr = base64.split(',')
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png'
    const bstr = atob(arr[1])
    let n = bstr.length
    const u8arr = new Uint8Array(n)
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n)
    }
    return new Blob([u8arr], { type: mime })
  }

  fileIsImage(file: UploadFile) {
    return IMAGE_EXTENSION.includes(file?.name?.split('.')[file?.name?.split('.').length - 1] || '')
  }

  async preupload(rawFile: ElFile) {
    // File kind is scan frame so we need to re-assign uuid for uploading after click submit button instead
    if (this.isScannedBCAvail && this.fileKind === EFileKind.BARCODE_SCAN_FRAME) {
      this.totalImagesNeedProcess++
      this.fileList.forEach((element, idx) => {
        if (element.name === rawFile.name) {
          this.fileList[idx].uid = rawFile.uid
        }
      })
    }

    // If not image return original file
    if (!this.isImageFileKind) return rawFile
    try {
      const resizedBlob = (await resizeImage({ maxSize: MAX_IMAGE_SIZE, file: rawFile })) as Blob
      return resizedBlob || rawFile
    } catch (error) {
      return rawFile
    }
  }

  beforeRemove(file: IFile) {
    if (file) {
      this.imageUrls.delete(getFilePathFromURL(file.url || ''))
      this.imageUrls.delete(file.response || '')

      this.fileList = this.fileList.filter((element) => element.url !== file.url)
      this.listResponsedImage = this.listResponsedImage.filter((element) => element !== file.url)
      if (this.listScannedBCFileFromStore.length > 0) {
        this.listScannedBCFileFromStore = this.listScannedBCFileFromStore.filter(
          (element) => element.name !== file.name
        )
      }

      // Remove empty strings after filtering
      this.removeEmptyStrings()

      return false
    }
    return true
  }

  async onRemove(file: IFile) {
    try {
      const remoteUrl = file?.response ?? ''
      if (!remoteUrl) return
      await removeImage(remoteUrl)
      this.imageUrls.delete(remoteUrl)
      this.fileList = this.fileList.filter((element) => element.response !== remoteUrl)
      this.listResponsedImage = this.listResponsedImage.filter((element) => element !== remoteUrl)

      // Remove empty strings after filtering
      this.removeEmptyStrings()
    } catch (error) {
      errorHandler(error as TError)
    }
  }

  async onError(error: Error) {
    // distinct handle max size 1015 error on server
    try {
      const msg = JSON.parse(error.message)
      const err = msg.errors?.File?.[0] ?? ''
      if (err.includes('1015')) {
        const byteNum = err.substring(err.indexOf('(') + 1, err.indexOf(')'))
        errorHandler('1015', { val1: byteNum })
      } else {
        errorHandler(err)
      }
    } catch (err) {
      errorHandler(error)
    }
  }

  onChanged(file: IFile, files: UploadFile[]) {
    if (file.status === 'success' || file.status === 'error') {
      this.totalImagesNeedProcess--
    }
    // Wait for all files upload completed (totalImagesNeedProcess = 0)
    if (this.isScannedBarcodeFrame) {
      if (!this.totalImagesNeedProcess) {
        this.handleSuccessImages(files)
      }
      return
    }
    if (file.status === 'success') {
      this.handleSuccessImages(files)
    }
  }

  handleSuccessImages(files: UploadFile[]) {
    if (this.fileKind !== EFileKind.BARCODE_SCAN_FRAME) {
      this.fileList = files
    }
    const uploadedResponse: { url: string }[] = []
    this.listResponsedImage.forEach((element) => {
      uploadedResponse.push({ url: element })
      this.imageUrls.add(element)
    })
    this.fileList.forEach((element, index) => {
      element.response = this.listResponsedImage[index]
    })

    // Remove empty strings after processing
    this.removeEmptyStrings()

    if (this.isScannedBarcodeFrame) {
      this.$emit('uploaded', uploadedResponse)
    }
  }

  onPreview(file?: IFile) {
    window.open(file?.url || '', '_blank')
  }

  @Watch('imageUrls', { deep: true })
  onImageChange() {
    // Remove empty strings before emitting updates
    this.removeEmptyStrings()

    mitt.emit('update:trackInfo', { [this.fieldName]: Array.from(this.imageUrls) })
    mitt.emit('update:fileList', { [this.fieldName]: Array.from(this.fileList) })
  }

  // @Watch('$store.getters.scannedBarcodeBlob')
  displayBarcodeImage() {
    if (this.scannedBCFile) {
      this.listResponsedImage = []
      const urlCreator = window.URL || window.webkitURL
      for (let i = 0; i < this.scannedBCFile.length; i++) {
        const file = this.scannedBCFile[i]

        const blobUrl = urlCreator.createObjectURL(file)
        const scannedBarcodeBlobs = this.$store.getters.scannedBarcodeBlob
        this.saveBlobsToLocalStorage(scannedBarcodeBlobs)
        this.fileList.push({
          name: `${this.barcodeId}_${i}.png`,
          url: blobUrl,
        } as UploadFile)
      }
    }
  }

  get barcode() {
    return this.$store.state.barcode.barcode
  }

  get currentTrackPointKey() {
    return this.barcode?.currentTrackPointKey
  }

  get project() {
    return this.$store.state.project.details
  }

  get keyBarcodeScanChanged() {
    return (
      this.project.code + '.' + this.barcode.id + '.trackingData.' + this.currentTrackPointKey + '.scannedBarcodeBlobs'
    )
  }

  @Watch('scannedBarcodeBlobsFromLocalStorage')
  scannedBCChanged() {
    this.isBarcodeScanFrame = this.fileKind === EFileKind.BARCODE_SCAN_FRAME
    if (this.isBarcodeScanFrame) this.displayBarcodeImage()
  }

  @Watch('$store.getters.customFieldsOcrImagesBlobs')
  customFieldOcrImageChanged() {
    const customFieldsOcrImagesBlobs = this.$store.getters.customFieldsOcrImagesBlobs
    if (this.fieldName in customFieldsOcrImagesBlobs) {
      const blobs = customFieldsOcrImagesBlobs[this.fieldName]
      const urlCreator = window.URL || window.webkitURL
      for (let i = 0; i < blobs.length; i++) {
        const scannedBarcodeBlob = blobs[i]
        const newFile = new File([scannedBarcodeBlob], `${this.fieldName}_${i}.png`, { type: 'image/png' })
        const blobUrl = urlCreator.createObjectURL(newFile)
        this.fileList.push({
          name: `${this.barcodeId}_${i}.png`,
          url: blobUrl,
        } as UploadFile)
      }
    }
  }

  get isPhotoOnlyKind() {
    return this.fileKind === EFileKind.PHOTO_ONLY
  }

  mounted() {
    this.scannedBCChanged()

    this.imageUrls = new Set()
    try {
      if (isEmpty(this.modelValue)) return
      this.fileList.push(
        ...(((this.modelValue as UploadFile[]) || [])
          .map((val) => {
            // Skip empty URLs
            if (!val.url || val.url === '') return null

            const dataFile = { name: val.name, url: val.url, response: val.url } as UploadFile
            this.imageUrls.add(getFilePathFromURL(val.url || ''))

            return dataFile
          })
          .filter((file): file is UploadFile => file !== null) || [])
      )

      // Remove any empty strings that might have been added
      this.removeEmptyStrings()
    } catch (error) {
      errorHandler(error as TError)
    }
  }

  onAddScannedBCImg() {
    if (this.scannedBCFile) {
      this.displayBarcodeImage()
    } else {
      this.showDialogAlert = true
    }
  }

  async httpRequestController(option: ElUploadRequestOptions) {
    const formData = new FormData()
    formData.append('barcodeId', this.barcodeId)
    formData.append('file', option.file)
    const request = {
      url: option.action,
      method: 'post',
      headers: option.headers as unknown,

      data: formData,
      onUploadProgress: (progressEvent: ElUploadProgressEvent) => {
        progressEvent.percent = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        option.onProgress(progressEvent)
      },
      withCredentials: true,
      timeout: 1800000, // 30'
    } as AxiosRequestConfig
    const response = await customRequest(request)
    this.listResponsedImage.push(response.toString())
    return response
  }
}
</script>

<style lang="scss" scoped>
@import '~@/assets/css/mixins.scss';

:deep() {
  .scanned-bc {
    height: 148px;
    width: 148px;
    border-radius: 4px;
    border: 1px solid lightblue;
    margin: 5px 0px;

    &-image {
      width: 100%;
      height: 100%;
      object-fit: contain;
      position: relative;
      z-index: 0;
    }
  }

  // Because we use customization button
  .hide-upload-btn {
    .el-upload--picture-card {
      display: none;
    }
  }

  .uploader-btn {
    background-color: $dark-grey;
    color: white;
    border: none;
    border-radius: 10px;
    width: 100%;
    max-height: 80px;
  }

  .el-upload {
    background-color: $dark-grey;
    color: white;

    &--picture-card {
      border: none;
      border-radius: 10px;
      width: 100%;
      max-height: 80px;
    }

    &-list {
      &__item {
        margin: 5px;

        &-status-label {
          top: 4px;
          right: 4px;
          width: 16px;
          height: 16px;
          background: #27ae60;
          border-radius: 999px;
          box-shadow: 0 0 1pc 1px rgba(214, 208, 208, 0.2);

          i {
            position: absolute;
            right: 1px;
            bottom: 1px;
            font-weight: bold;
          }
        }
      }
    }
  }
}

.margin-30 {
  margin-top: 30%;
}

.action-button {
  color: $pure-white;
  margin-top: 12px;

  svg {
    display: block;
    width: 100%;
    margin-bottom: -60px;
  }
}

.scanned-barcode-warning-dialog-ok {
  min-height: 60px;
  font-size: 22px;
}
</style>
